'use client';

import { useState, useEffect } from 'react';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className={`relative bg-gradient-to-br from-primary via-secondary to-primary overflow-hidden ${className}`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,212,255,0.1),transparent_50%)]"></div>
        </div>
        <div className="relative mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted/50 rounded-full border border-accent/30 mb-8">
              <div className="w-2 h-2 bg-accent rounded-full"></div>
              <span className="font-cta text-xs text-accent tracking-wider">MISSION CONTROL FOR MANUFACTURING</span>
            </div>
            <h1 className="font-headline text-4xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Where Aerospace Dreams Meet Manufacturing Reality
            </h1>
            <p className="font-body text-lg lg:text-xl text-technical-gray max-w-3xl mx-auto leading-relaxed">
              Techaero embodies the convergence of aerospace ambition and manufacturing mastery, representing unwavering precision, breakthrough velocity, and engineering excellence that powers humanity's greatest aerospace achievements.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`relative bg-gradient-to-br from-primary via-secondary to-primary overflow-hidden ${className}`}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,212,255,0.1),transparent_50%)]"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent/5 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-mission-orange/5 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>

      <div className="relative mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-muted/50 rounded-full border border-accent/30 mb-8 animate-fade-in">
            <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
            <span className="font-cta text-xs text-accent tracking-wider">MISSION CONTROL FOR MANUFACTURING</span>
          </div>

          <h1 className="font-headline text-4xl lg:text-6xl font-bold text-foreground mb-6 leading-tight animate-fade-in-up">
            Where Aerospace Dreams Meet <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-mission-orange">Manufacturing Reality</span>
          </h1>

          <p className="font-body text-lg lg:text-xl text-technical-gray max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Techaero embodies the convergence of aerospace ambition and manufacturing mastery, representing unwavering precision, breakthrough velocity, and engineering excellence that powers humanity's greatest aerospace achievements.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { label: 'Years of Excellence', value: '15+' },
              { label: 'Aerospace Projects', value: '500+' },
              { label: 'Manufacturing Partners', value: '50+' }
            ].map((stat, index) => (
              <div key={index} className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6 hover:border-accent transition-all duration-300 hover:shadow-glow">
                <div className="font-accent text-3xl lg:text-4xl font-bold text-accent mb-2">{stat.value}</div>
                <div className="font-body text-sm text-technical-gray">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;